import * as React from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";

import { styleGuideOn } from "./app/styleGuide";
import Layout from "./components/shell/Layout";
import ProtectedRoute from "./components/shell/ProtectedRoute";
import ScrollMemory from "./components/shell/ScrollMemory";
import WorkModal from "./components/works/WorkModal";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { rememberRef } from "./data/share";

/* istanbul ignore next */
const HomePage = React.lazy(() => import("./pages/HomePage"));
/* istanbul ignore next */
const BrowsePage = React.lazy(() => import("./pages/BrowsePage"));
/* istanbul ignore next */
const WorkPage = React.lazy(() => import("./pages/WorkPage"));
/* istanbul ignore next */
const SeriesPage = React.lazy(() => import("./pages/SeriesPage"));
/* istanbul ignore next */
const ContributorPage = React.lazy(() => import("./pages/ContributorPage"));
/* istanbul ignore next */
const LibraryPage = React.lazy(() => import("./pages/LibraryPage"));
/* istanbul ignore next */
const LibraryStatusPage = React.lazy(() => import("./pages/LibraryStatusPage"));
/* istanbul ignore next */
const ScanPage = React.lazy(() => import("./pages/ScanPage"));
/* istanbul ignore next */
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
/* istanbul ignore next */
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
/* istanbul ignore next */
const SignupPage = React.lazy(() => import("./pages/SignupPage"));
/* istanbul ignore next */
const ForgotPasswordPage = React.lazy(
  () => import("./pages/ForgotPasswordPage"),
);
/* istanbul ignore next */
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
/* istanbul ignore next */
const VerifyEmailPage = React.lazy(() => import("./pages/VerifyEmailPage"));
/* istanbul ignore next */
const OAuthPage = React.lazy(() => import("./pages/OAuthPage"));
/* istanbul ignore next */
const SegmentPage = React.lazy(() => import("./pages/SegmentPage"));
/* istanbul ignore next */
const ProfilePage = React.lazy(() => import("./pages/ProfilePage"));
/* istanbul ignore next */
const FollowsPage = React.lazy(() => import("./pages/FollowsPage"));
/* istanbul ignore next */
const FeedPage = React.lazy(() => import("./pages/FeedPage"));
/* istanbul ignore next */
const ListsPage = React.lazy(() => import("./pages/ListsPage"));
/* istanbul ignore next */
const ListPage = React.lazy(() => import("./pages/ListPage"));
/* istanbul ignore next */
const GuidesPage = React.lazy(() => import("./pages/GuidesPage"));
/* istanbul ignore next */
const GuidePage = React.lazy(() => import("./pages/GuidePage"));
/* istanbul ignore next */
const BlogPage = React.lazy(() => import("./pages/BlogPage"));
/* istanbul ignore next */
const PostPage = React.lazy(() => import("./pages/PostPage"));
/* istanbul ignore next */
const ChangelogPage = React.lazy(() => import("./pages/ChangelogPage"));

const PrivacyPage = React.lazy(() => import("./pages/PrivacyPage"));
/* istanbul ignore next */
const SourcesPage = React.lazy(() => import("./pages/SourcesPage"));
/* istanbul ignore next */
const StatsPage = React.lazy(() => import("./pages/StatsPage"));
/* istanbul ignore next */
const WelcomePage = React.lazy(() => import("./pages/WelcomePage"));
/* istanbul ignore next */
const RecapPage = React.lazy(() => import("./pages/RecapPage"));
const DiaryPage = React.lazy(() => import("./pages/DiaryPage"));
/* istanbul ignore next */
const TakePage = React.lazy(() => import("./pages/TakePage"));
/* istanbul ignore next */
const NotFoundPage = React.lazy(() => import("./pages/NotFoundPage"));
/* istanbul ignore next */
const DesignPage = React.lazy(() => import("./pages/DesignPage"));

export function AppRoutes() {
  const location = useLocation();
  const background = location.state?.background;
  // A shared link's ?ref= names who shared it, for the signup it may bring.
  React.useEffect(() => rememberRef(location.search), [location.search]);

  return (
    <>
      <Routes location={background || location}>
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          {/* Keyed so each tab starts from its own URL's search. */}
          <Route path="/browse" element={<BrowsePage key="titles" />} />
          <Route
            path="/browse/people"
            element={<BrowsePage key="people" scope="people" />}
          />
          <Route
            path="/browse/lists"
            element={<BrowsePage key="lists" scope="lists" />}
          />
          <Route path="/works/:id" element={<WorkPage />} />
          <Route path="/works/:id/segments/:segId" element={<SegmentPage />} />
          <Route path="/series/:id" element={<SeriesPage />} />
          <Route path="/contributors/:id" element={<ContributorPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/oauth" element={<OAuthPage />} />
          <Route path="/u/:username" element={<ProfilePage />} />
          <Route path="/u/:username/stats" element={<StatsPage />} />
          <Route path="/u/:username/year/:year" element={<RecapPage />} />
          <Route path="/u/:username/diary" element={<DiaryPage />} />
          <Route path="/u/:username/works/:id" element={<TakePage />} />
          <Route
            path="/u/:username/followers"
            element={<FollowsPage key="followers" which="followers" />}
          />
          <Route
            path="/u/:username/following"
            element={<FollowsPage key="following" which="following" />}
          />
          <Route path="/lists/:id" element={<ListPage />} />
          <Route path="/guides" element={<GuidesPage />} />
          <Route path="/guides/:name" element={<GuidePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<PostPage />} />
          <Route path="/changelog" element={<ChangelogPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/sources" element={<SourcesPage />} />
          {styleGuideOn() && <Route path="/design" element={<DesignPage />} />}
          <Route element={<ProtectedRoute />}>
            <Route path="/feed" element={<FeedPage />} />
            <Route path="/library" element={<LibraryPage />} />
            <Route path="/library/:status" element={<LibraryStatusPage />} />
            <Route path="/lists" element={<ListsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/scan" element={<ScanPage />} />
            <Route path="/welcome" element={<WelcomePage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      {background && <WorkModal />}
      <ScrollMemory />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
